import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FaGoogle } from "react-icons/fa6";
import { MdClose, MdPower, MdPowerOff } from "react-icons/md";
import ShinyCard from "src/components/BankCard/ShinyCard";
import Modal from "src/components/Modal/Modal";
import useModalsStore from "src/store/modalsStore";
import { useGoogleCodeFlow } from "./googleAuth";
import { useCallback, useState } from "react";
import { exchangeTokens, isRevoked, sendEmail } from "src/api/google";
import { showNotification } from "src/helpers/utils";
import useCurrentUser from "src/hooks/useCurrentUser";
import ButtonGroup from "src/components/ButtonGroup/ButtonGroup";
import { useGmailConnectorStatus } from "./useGmailConnectorStatus";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "src/components/Button/Button.types";

const GmailConnectorModal: React.FC = () => {
  const { t } = useTranslation(["extensions"]);
  const { setShowGmailConnectorModal } = useModalsStore();
  const { userInfo } = useCurrentUser();
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);

  const { isLoadingGmail, isGmailConnected, canSendGmail, refresh } =
    useGmailConnectorStatus();

  const onCode = useCallback(
    async (code: string) => {
      setIsConnecting(true);
      try {
        const r = await exchangeTokens(code);

        if (!r?.data?.ok) {
          showNotification({
            kind: "error",
            message: t("gmailConnector.on_code.error"),
          });
          return;
        }

        // Revalida estado inmediatamente
        await refresh();

        const send = await sendEmail({
          to: userInfo?.email || "",
          subject: t("gmailConnector.test.subject"),
          text: t("gmailConnector.test.text"),
          html: t("gmailConnector.test.html"),
        });

        if (send?.data?.ok) {
          showNotification({
            kind: "success",
            message: t("gmailConnector.on_code.success"),
          });
        } else {
          showNotification({
            kind: "error",
            message: t("gmailConnector.on_code.error"),
          });
        }
      } catch (e) {
        showNotification({
          kind: "error",
          message: t("gmailConnector.on_code.error"),
        });
      } finally {
        setIsConnecting(false);
      }
    },
    [refresh, userInfo?.email, t],
  );

  const { trigger } = useGoogleCodeFlow(onCode);

  return (
    <Modal
      onEscape={() => setShowGmailConnectorModal(false)}
      className={clsx(["md:!w-full", "md:!max-w-[400px]"])}
    >
      <Modal.Header
        onClose={() => setShowGmailConnectorModal(false)}
        title={t("gmailConnector.title")}
        isClosable={!isConnecting || !isDisconnecting}
      />

      <Modal.Body>
        <div
          className={clsx(["flex", "w-full", "flex-col", "gap-4", "md:gap-6"])}
        >
          <div className={clsx(["flex", "flex-col", "w-full", "gap-4"])}>
            <div
              className={clsx([
                "flex",
                "w-full",
                "items-center",
                "flex-row",
                "gap-4",
              ])}
            >
              <div
                className={clsx([
                  "rounded",
                  "inline-flex",
                  "h-[70px]",
                  "w-[70px]",
                  "items-center",
                  "justify-center",
                  "dark:bg-black",
                  "bg-gray-200",
                ])}
              >
                <ShinyCard>
                  <div
                    className={clsx([
                      "h-full",
                      "w-full",
                      "items-center",
                      "justify-center",
                      "flex",
                    ])}
                  >
                    <FaGoogle size={ICON_SIZES.LG} />
                  </div>
                </ShinyCard>
              </div>
              <div className={clsx(["text-left"])}>
                <div className={clsx(["font-medium"])}>
                  {t("gmailConnector.title")}
                </div>
                <div className={clsx(["text-xs"])}>
                  {isLoadingGmail
                    ? t("gmailConnector.status.loading")
                    : isGmailConnected
                      ? t("gmailConnector.status.enabled")
                      : t("gmailConnector.status.disabled")}
                </div>
              </div>
            </div>

            <div className={clsx(["flex", "flex-col", "gap-4"])}>
              <div className={clsx(["text-sm"])}>
                {t("gmailConnector.description")}
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer
        leftContent={
          <ButtonGroup
            buttons={
              isLoadingGmail
                ? []
                : [
                    {
                      text: t("gmailConnector.buttons.disable"),
                      onClick: async () => {
                        setIsDisconnecting(true);
                        try {
                          const res = await isRevoked();
                          if (res?.data?.ok) {
                            showNotification({
                              kind: "success",
                              message: t("gmailConnector.on_revoke.success"),
                            });
                          } else {
                            showNotification({
                              kind: "error",
                              message: t("gmailConnector.on_revoke.error"),
                            });
                          }
                          // Revalida estado tras revocar
                          await refresh();
                        } catch {
                          showNotification({
                            kind: "error",
                            message: t("gmailConnector.on_revoke.error"),
                          });
                        } finally {
                          setIsDisconnecting(false);
                        }
                      },
                      kind: EButtonKind.DANGER,
                      icon: <MdPowerOff size={ICON_SIZES.SM} />,
                      isLoading: isDisconnecting,
                      disabled: isDisconnecting,
                      hide: !canSendGmail,
                    },
                    {
                      text: t("gmailConnector.buttons.enable"),
                      onClick: trigger,
                      kind: EButtonKind.SAVE,
                      icon: <MdPower size={ICON_SIZES.SM} />,
                      isLoading: isConnecting,
                      disabled: isConnecting,
                      hide: canSendGmail,
                    },
                  ]
            }
          />
        }
        actions={[
          {
            text: t("common:buttons.close"),
            onClick: () => setShowGmailConnectorModal(false),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
            disabled: isConnecting || isDisconnecting,
          },
        ]}
      />
    </Modal>
  );
};

export default GmailConnectorModal;
